<template>
  <div class="total-barchart-canvas">
    <canvas
      ref="totalBarchartCanvas"
      @mouseleave="resetOnMouseOut"
      @mouseout="resetOnMouseOut"
    ></canvas>
    <div
      v-show="showTooltip"
      ref="totalBarchartTooltip"
      class="total-barchart-canvas__tooltip i-radius-md i-p-md"
    >
      <p class="i-text-body-3 i-mb-1">{{ tooltipTitle }}</p>
      <p class="i-text-subheading-1 i-m-0">{{ tooltipContent }}</p>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { toCurrency, centsToCurrencyShorthand } from '@/utils/toCurrency';
import palette from '@inscribe/signal/dist/palette';
import moment from 'moment';
import { $event } from '@/bus';
import {
  downloadChart,
  downloadPDF,
  printChart,
  toggleFullscreen,
} from '@/utils/chartUtils';

const originalColor = palette['light-primary-100'];
const axisColor = palette['light-neutral-110'];

export default {
  props: {
    chartView: {
      type: String,
      required: true,
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    chartTooltipTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      monthlyData: [],
      quarterlyData: [],
      showTooltip: false,
      tooltipTitle: '',
      tooltipContent: '',
    };
  },
  computed: {
    graphScalesConfiguration() {
      return {
        y: {
          border: {
            color: axisColor,
          },
          ticks: {
            padding: 8,
            callback: function (value) {
              return centsToCurrencyShorthand(value, 1);
            },
            maxTicksLimit: 8,
            color: axisColor,
          },
          beginAtZero: true,
          grid: {
            color: axisColor,
            display: true,
            drawOnChartArea: false,
            drawTicks: true,
          },
        },
        x: {
          border: {
            color: axisColor,
          },
          grid: {
            color: axisColor,
            display: true,
            drawOnChartArea: false,
            drawTicks: false,
          },
          ticks: {
            padding: 24,
          },
        },
      };
    },
  },
  watch: {
    chartView: {
      handler(newType, oldType) {
        if (newType !== oldType) {
          this.updateChart(newType);
        }
      },
      deep: true,
    },
  },
  created() {
    $event.$on('total-barchart-menu-event', this.handleMenuEvent);
  },
  beforeDestroy() {
    $event.$on('total-barchart-menu-event', this.handleMenuEvent);
  },
  mounted() {
    this.createChart(this.chartView);
  },
  methods: {
    createChart(view) {
      const chartCanvas = this.$refs.totalBarchartCanvas;
      this.monthlyData = this.formatMonthLabel(this.chartData);
      this.quarterlyData = this.groupTransactionsByQuarter(this.chartData);

      this.chart = new Chart(chartCanvas, {
        type: 'bar',
        data: {
          datasets: this.chartDataset(view),
        },
        options: this.graphOptionsConfiguration(),
      });
    },

    resetOnMouseOut() {
      // Reset all bar colors to their original colors
      this.chart.data.datasets[0].backgroundColor =
        this.chart.data.datasets[0].data.map(() => originalColor);
      // Update the chart to apply the original colors
      this.chart.update();

      // Hide the tooltip when mouse leaves the chart area
      if (this.showTooltip) {
        this.showTooltip = false;
      }
    },

    updateChart(view) {
      this.chart.data.datasets = this.chartDataset(view);
      this.chart.update();
    },

    chartDataset(view) {
      const data = view === 'quarterly' ? this.quarterlyData : this.monthlyData;

      return [
        {
          data: [...data],
          backgroundColor: [originalColor],
        },
      ];
    },

    graphOptionsConfiguration() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          tooltip: {
            enabled: false,
          },
          legend: {
            display: false,
            onClick: null,
          },
        },
        scales: this.graphScalesConfiguration,
        onHover: this.onChartHover,
      };
    },

    onChartHover(event, chartElement) {
      // Check if there's a hovered bar
      if (chartElement.length > 0) {
        const activeIndex = chartElement[0].index;

        // Reset all bar colors to light-primary-60
        this.chart.data.datasets[0].backgroundColor =
          this.chart.data.datasets[0].data.map(
            () => palette['light-primary-60'],
          );

        const yValue = this.chart.data.datasets[0].data[activeIndex].y;
        const xValue = this.chart.data.datasets[0].data[activeIndex].x;

        if (this.chartView === 'quarterly') {
          this.tooltipTitle = `${
            this.chartTooltipTitle
          } ${this.parseQuarterLabel(xValue)}`;
        } else {
          this.tooltipTitle = this.chartTooltipTitle;
        }
        this.tooltipContent = `${toCurrency(yValue)}`;
        this.showTooltip = true;

        // Position the tooltip near the hovered bar
        this.positionTooltip(event, chartElement[0].element);

        // Set the hovered bar color to its original color
        this.chart.data.datasets[0].backgroundColor[activeIndex] =
          originalColor;

        // Update the chart to apply the new colors
        this.chart.update();
      }
    },

    formatQuarterLabel(date) {
      const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
      const d = new Date(date);
      return `${quarters[Math.floor(d.getMonth() / 3)]} '${d
        .getFullYear()
        .toString()
        .slice(2)}`;
    },

    formatMonthLabel(transactions) {
      return transactions.map((transaction) => ({
        x: moment(transaction.x).format("MMM 'YY"),
        y: transaction.y,
      }));
    },

    groupTransactionsByQuarter(transactions) {
      const quartersData = transactions.reduce((acc, transaction) => {
        const quarter = this.formatQuarterLabel(transaction.x);
        if (!acc[quarter]) {
          acc[quarter] = 0;
        }
        acc[quarter] += transaction.y;
        return acc;
      }, {});

      return Object.keys(quartersData).map((quarter) => ({
        x: quarter,
        y: quartersData[quarter],
      }));
    },

    positionTooltip(event, activeElement) {
      const tooltip = this.$refs.totalBarchartTooltip;

      if (activeElement.width > 160) {
        tooltip.style.width = activeElement.width + 'px';
      } else {
        tooltip.style.width = null;
      }

      tooltip.style.left = activeElement.x + 'px';
      tooltip.style.top = activeElement.y + 'px';
    },

    parseQuarterLabel(quarterLabel) {
      const [quarter] = quarterLabel.split(" '");

      const quarters = {
        Q1: ['Jan', 'Mar'],
        Q2: ['Apr', 'Jun'],
        Q3: ['Jul', 'Sep'],
        Q4: ['Oct', 'Dec'],
      };

      if (quarters[quarter]) {
        const [firstMonth, lastMonth] = quarters[quarter];

        return `(${firstMonth} - ${lastMonth})`;
      }
    },

    handleMenuEvent(menuSelection) {
      switch (menuSelection) {
        case 'fullscreen':
          toggleFullscreen(this.$refs.totalBarchartCanvas);
          break;
        case 'print_chart':
          printChart(this.$refs.totalBarchartCanvas);
          break;
        case 'download_png':
          this.downloadChartImage('png');
          break;
        case 'download_jpeg':
          this.downloadChartImage('jpg');
          break;
        case 'download_pdf':
          this.downloadChartPdf();
          break;
      }
    },

    downloadChartImage(extension) {
      const canvas = this.$refs.totalBarchartCanvas;
      const fileName = this.getGraphFileName(extension);
      downloadChart(canvas, fileName, `image/${extension}`);
    },

    downloadChartPdf() {
      const extension = 'pdf';
      const canvas = this.$refs.totalBarchartCanvas;
      const fileName = this.getGraphFileName(extension);
      const chartDescription = `Inscribe: ${this.tooltipTitle}`;
      downloadPDF(canvas, fileName, chartDescription);
    },

    getGraphFileName(extension) {
      const graphName = this.chartTooltipTitle.toLowerCase().replace(' ', '_');
      const viewType = this.chartView.toLowerCase();
      const timestamp = moment().format('YYYY-MM-DD');
      const fileName = `total_${graphName}_${viewType}_${timestamp}`;
      return `${fileName}.${extension}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.total-barchart-canvas {
  position: relative;
  height: 320px;

  canvas {
    background-color: $light-neutral-default;
  }

  &__tooltip {
    position: absolute;
    min-width: 160px;
    text-align: center;
    background-color: $light-neutral-default;
    border: 1px solid $light-neutral-60;
    transform: translate(-50%, -110%);
    box-shadow: 0px 1px 20px 0px rgba($light-neutral-110, 0.15);
    z-index: 999;
  }
}
</style>
